import { Box, Typography, List, TextField, Button, FormControlLabel, Checkbox, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { useState, useEffect } from "react";
import { getData, deleteData } from "../api";
import theme from "../styles/theme";

interface Comment {
  id: number;
  date_comment: string;
  staff_id: string;
  visible_to_player: number;
  comment: string;
}

interface CommentsListProps {
  comments: Comment[];
  onAddComment?: (comment: Omit<Comment, "id" | "date_comment">) => void;
}



const CommentsList = ({ comments, onAddComment }: CommentsListProps) => {
  // Suppression de commentaire
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const handleDeleteClick = (id: string | number) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };
  const handleDeleteCancel = () => {
    setDeleteId(null);
    setConfirmOpen(false);
  };
  const handleDeleteConfirm = async () => {
    if (deleteId !== null) {
      try {
        // Utilisation d'une clause sécurisée (liaison de paramètre)
        await deleteData("comments", `id = '${deleteId}'`);
      } catch (e) {
        // Optionnel : afficher une notification d'échec
      }
      setLocalComments((prev: any) => prev.filter((c: any) => String(c.id) !== String(deleteId)));
    }
    setDeleteId(null);
    setConfirmOpen(false);
  };

  // Pour la suppression locale immédiate (optimiste)
  const [localComments, setLocalComments] = useState(comments);
  useEffect(() => { setLocalComments(comments); }, [comments]);
  const userLogged = localStorage.getItem("user");
  const parsedUser = userLogged ? JSON.parse(userLogged) : null;

  // Pour associer staff_id -> nom complet
  const [staffNames, setStaffNames] = useState<{ [id: string]: string }>({});

  useEffect(() => {
    // Extraire tous les staff_id uniques
    const ids = Array.from(new Set(comments.map(c => c.staff_id).filter(Boolean)));
    if (ids.length === 0) return;
    // Requête API pour récupérer les noms
    getData("users", { where: `id IN (${ids.map(id => `'${id}'`).join(",")})` }).then((res: any) => {
      const map: { [id: string]: string } = {};
      (res.data || []).forEach((u: any) => {
        map[u.id] = u.nom_complet || u.username || u.name || u.id;
      });
      setStaffNames(map);
    });
  }, [comments]);

  // Formulaire d'ajout de commentaire
  const [newComment, setNewComment] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    // Appel API ou callback parent
    if (onAddComment) {
      onAddComment({
        staff_id: parsedUser?.id || "",
        visible_to_player: isPublic ? 1 : 0,
        comment: newComment.trim(),
      });
    }
    setNewComment("");
    setIsPublic(true);
    setSubmitting(false);
  };

  return (
    <>
      {(["coach", "staff"].includes(parsedUser?.role || "")) && (
        <Box sx={{ mb: 3, p: 2, boxShadow: 1, padding: "10px !important", borderRadius: 2, background: theme.palette.background.paper }}>
          <Typography variant="h3" sx={{ fontSize: "1.1rem", mb: 1, color: theme.palette.primary.main, textAlign: "center" }}>
            Ajouter un commentaire
          </Typography>
          <TextField
            label="Votre commentaire"
            multiline
            minRows={2}
            fullWidth
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel style={{ marginLeft: 0, marginRight: 'auto', display: 'block' }}
            control={<Checkbox checked={isPublic} onChange={e => setIsPublic(e.target.checked)} color="primary" />}
            label="Rendre ce commentaire visible au joueur"
          />
            <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddComment}
              disabled={submitting || !newComment.trim()}
              sx={{ ml: 2 }}
            >
              Ajouter
            </Button>
            </Box>
        </Box>
      )}
      <List sx={{ width: "100%", p: 0 }}>
        {localComments
          .filter((comment) => {
            // Public : visible à tous
            if (comment.visible_to_player === 1) return true;
            // Privé : visible uniquement par le staff/coach OU l'auteur du commentaire
            if (["coach", "staff"].includes(parsedUser?.role || "")) return true;
            if (String(comment.staff_id) === String(parsedUser?.id)) return true;
            return false;
          })
          .sort((a, b) => new Date(b.date_comment).getTime() - new Date(a.date_comment).getTime())
          .map((comment) => (
            <Box
              key={comment.id}
              sx={{
                mb: 2,
                borderLeft: `6px solid ${comment.visible_to_player === 1 ? theme.palette.primary.main : theme.palette.warning.main}`,
                boxShadow: "0 2px 8px rgba(102,41,192,0.07)",
                borderRadius: 2,
                padding: 2.5,
                background: theme.palette.background.paper ,
                transition: "box-shadow 0.2s",
                '&:hover': {
                  boxShadow: "0 4px 16px rgba(102,41,192,0.13)",
                  background: theme.palette.action.hover,
                },
                position: "relative",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.08rem",
                  color: theme.palette.primary.light,
                  mb: 0.5,
                  letterSpacing: 0.2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingLeft:"8px",
                  paddingRight:"8px",
                  gap: 1,
                }}
              >
                <span style={{fontWeight: 400, color: theme.palette.text.secondary, fontSize: "0.98rem", textAlign: "left"}}>
                  {new Date(comment.date_comment).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })} de <b>{staffNames[comment.staff_id] || comment.staff_id || "Utilisateur inconnu"}</b>
                </span>
                {String(comment.staff_id) === String(parsedUser?.id) && (
                  <IconButton size="small" sx={{ ml: 1 }} onClick={() => handleDeleteClick(comment.id)} aria-label="Supprimer le commentaire">
                    <DeleteIcon fontSize="small" color="primary" style={{ color: theme.palette.text.primary }} />
                  </IconButton>
                )}
              </Typography>
              
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: "1.08rem",
                    lineHeight: 1.4,
                    whiteSpace: "pre-line",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    pl: 1,
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                    width: "93%",
                  }}
                >
                  <span
                    style={{
                      fontSize: "1.5rem",
                      color: theme.palette.primary.light,
                      fontFamily: "serif",
                      marginRight: 4,
                      lineHeight: 1,
                      userSelect: "none",
                      alignSelf: "flex-start",
                    }}
                  >
                    &ldquo;
                  </span>
                  <span style={{ flex: 1 }}>{comment.comment}</span>
                  <span
                    style={{
                      fontSize: "1.5rem",
                      color: theme.palette.primary.light,
                      fontFamily: "serif",
                      marginLeft: 2, // réduit l'espace à gauche du guillemet droit
                      lineHeight: 1,
                      userSelect: "none",
                      alignSelf: "flex-end",
                    }}
                  >
                    &rdquo;
                  </span>
                </Typography>
              {comment.visible_to_player === 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontStyle: "italic",
                    fontSize: "0.98rem",
                    color: theme.palette.warning.main,
                    display: "block",
                    textAlign: "center",
                    mb: 1,
                  }}
                >
                  [Com. visible uniquement par le staff]
                </Typography>
              )}
            </Box>
          ))}
      </List>

      {/* Modal de confirmation de suppression */}
      <Dialog open={confirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          Voulez-vous vraiment supprimer ce commentaire ? Cette action est irréversible.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">Annuler</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Supprimer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommentsList;
